import { useEffect, useState } from "react";
import { Send, Loader2, Eye } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

export default function NewsletterCompose({ subscribers }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const load = () => api.get("/newsletter/campaigns").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    if (!window.confirm(`Inviare "${subject}" a ${subscribers} iscritti?`)) return;
    setBusy(true);
    try {
      await api.post("/newsletter/campaigns", { subject, body });
      toast.success("Invio avviato: le email partono in background");
      setSubject(""); setBody(""); setPreview(false);
      setTimeout(load, 3000); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4" data-testid="newsletter-compose">
      <h3 className="font-serif text-xl font-semibold text-[#0B2A45] flex items-center gap-2"><Send className="w-5 h-5" />Scrivi e invia newsletter</h3>
      <div><Label className="text-sm">Oggetto</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Es. Novità d'estate: spritz in fusto e granite" className="mt-1" data-testid="campaign-subject-input" /></div>
      <div><Label className="text-sm">Testo (paragrafi separati da una riga vuota)</Label><Textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} className="mt-1" data-testid="campaign-body-input" /></div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" className="rounded-full" onClick={() => setPreview(!preview)} disabled={!body} data-testid="campaign-preview-btn"><Eye className="w-4 h-4 mr-2" />{preview ? "Nascondi anteprima" : "Anteprima"}</Button>
        <Button type="button" className="rounded-full bg-[#C2593F] hover:bg-[#9A3412] text-white" onClick={send} disabled={busy || subject.length < 3 || body.length < 10 || !subscribers} data-testid="campaign-send-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Invia a {subscribers} iscritti</>}
        </Button>
      </div>
      {preview && (
        <div className="rounded-xl border border-stone-200 bg-[#FAFAF7] p-5" data-testid="campaign-preview">
          <div className="text-xs text-stone-400 mb-2">Oggetto: <b className="text-[#0B2A45]">{subject || "(senza oggetto)"}</b></div>
          <h4 className="font-serif text-lg text-[#0F4C81] mb-3">Guatemalan Spirits & Fruit Juice</h4>
          {body.split("\n\n").filter(Boolean).map((p, i) => <p key={`p-${i}`} className="text-sm text-[#0B2A45] mb-3 whitespace-pre-line">{p}</p>)}
          <p className="text-xs text-[#C2593F]">Leggi la guida · Visita il negozio</p>
        </div>
      )}
      {history.length > 0 && (
        <div className="pt-4 border-t border-stone-100" data-testid="campaign-history">
          <div className="text-sm font-semibold text-[#0B2A45] mb-2">Storico invii</div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-stone-500 border-b"><th className="py-1.5 pr-3">Data</th><th className="py-1.5 pr-3">Oggetto</th><th className="py-1.5 pr-3">Destinatari</th><th className="py-1.5 pr-3">Inviate</th><th className="py-1.5">Stato</th></tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.campaign_id} className="border-b border-stone-100" data-testid={`campaign-row-${h.campaign_id}`}>
                  <td className="py-1.5 pr-3 text-stone-500">{(h.created_at || "").slice(0, 10)}</td>
                  <td className="py-1.5 pr-3 font-medium text-[#0B2A45]">{h.subject}</td>
                  <td className="py-1.5 pr-3">{h.recipients}</td>
                  <td className="py-1.5 pr-3">{h.sent}{h.failed ? <span className="text-red-600"> ({h.failed} fallite)</span> : ""}</td>
                  <td className="py-1.5"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${h.status === "sent" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{h.status === "sent" ? "Inviata" : "In corso"}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
